import type { TabsItem } from '#layers/ui/public';
import { randomUUID } from '#layers/base/public';

/** Tab value type supported by the Tabs block. */
export type TabsBlockItemValue = Extract<TabsItem['value'], string>;

/** A tab configured by the block editor. */
export interface TabsBlockItem {
	/** Display label shown in the tab trigger. */
	label?: TabsItem['label'];
	/** Selectable tab value forwarded to DTabs. */
	value?: TabsBlockItemValue;
	/** Stable block slot id used to preserve child content. */
	slotId?: string;
	/** Optional icon rendered before the tab label. */
	icon?: TabsItem['icon'];
	/** Prevents this tab from being selected. */
	disabled?: TabsItem['disabled'];
}

/** A configured tab item with normalized identifiers. */
export type NormalizedTabsBlockConfigItem = TabsBlockItem & Required<Pick<TabsBlockItem, 'value' | 'slotId'>>;

/** A normalized tab item rendered by DTabs. */
export interface NormalizedTabsBlockItem extends Required<Pick<TabsBlockItem, 'label' | 'value'>> {
	/** Stable block slot rendered by DTabs for this item. */
	slot: string;
	/** Stable block slot id used to preserve child content. */
	slotId: string;
	/** Optional icon rendered before the tab label. */
	icon?: string;
	/** Prevents this tab from being selected. */
	disabled?: boolean;
}

/** Result of normalizing configured tabs. */
export interface NormalizeTabsBlockItemsResult {
	/** Normalized items with unique stable slot ids. */
	items: NormalizedTabsBlockConfigItem[];
	/** Whether any item had to be changed. */
	changed: boolean;
}

/** Default selected tab for newly inserted blocks. */
export const DEFAULT_TABS_BLOCK_VALUE = 'tab-1';

/** Default tab configuration used for newly inserted blocks. */
const DEFAULT_TABS_BLOCK_ITEMS = [
	{ label: 'Tab 1', value: DEFAULT_TABS_BLOCK_VALUE, slotId: DEFAULT_TABS_BLOCK_VALUE },
	{ label: 'Tab 2', value: 'tab-2', slotId: 'tab-2' },
] satisfies TabsBlockItem[];

/**
 * Creates a new random tab slot id for newly added repeater rows.
 */
export function createTabsBlockSlotId(): string {
	return `tab-${randomUUID()}`;
}

/**
 * Returns a fresh default tab list so editor forms can mutate it safely.
 */
export function createDefaultTabsBlockItems(): TabsBlockItem[] {
	return DEFAULT_TABS_BLOCK_ITEMS.map((item) => ({ ...item }));
}

/**
 * Returns the tab item value used by DTabs.
 */
export function getTabsBlockItemValue(item: TabsBlockItem, index: number): string {
	return item.value || `tab-${index + 1}`;
}

/**
 * Returns the legacy slot id used before stable slot ids were stored.
 */
export function getLegacyTabsBlockSlotId(item: TabsBlockItem, index: number): string {
	return getTabsBlockItemValue(item, index);
}

/**
 * Returns configured items, falling back to the default two-tab setup.
 */
export function getTabsBlockItems(items: unknown): TabsBlockItem[] {
	return Array.isArray(items) && items.length > 0 ? items : createDefaultTabsBlockItems();
}

/**
 * Returns a unique tab identifier by suffixing a preferred value only when necessary.
 */
function getUniqueTabsBlockIdentifier(preferredValue: string, usedValues: Set<string>): string {
	let value = preferredValue;
	let suffix = 2;

	while (usedValues.has(value)) {
		value = `${preferredValue}-${suffix}`;
		suffix++;
	}

	return value;
}

/**
 * Adds stable unique values and slot ids to configured tab items.
 */
export function normalizeTabsBlockItems(items: unknown): NormalizeTabsBlockItemsResult {
	const usedValues = new Set<string>();
	const usedSlotIds = new Set<string>();
	let changed = false;

	const normalizedItems = getTabsBlockItems(items).map((item, index) => {
		const preferredValue = getTabsBlockItemValue(item, index);
		const value = getUniqueTabsBlockIdentifier(preferredValue, usedValues);
		const legacySlotId = getLegacyTabsBlockSlotId(item, index);
		const currentSlotId = item.slotId || undefined;
		const preferredSlotId = currentSlotId && !usedSlotIds.has(currentSlotId)
			? currentSlotId
			: legacySlotId;
		const slotId = getUniqueTabsBlockIdentifier(preferredSlotId, usedSlotIds);

		usedValues.add(value);
		usedSlotIds.add(slotId);

		if (item.value !== value || item.slotId !== slotId) {
			changed = true;
		}

		return {
			...item,
			slotId,
			value,
		};
	});

	return {
		items: normalizedItems,
		changed,
	};
}

/**
 * Builds block slot definitions from the current tab items.
 */
export function getTabsBlockSlots(items: TabsBlockItem[] | undefined) {
	return normalizeTabsBlockItems(items).items.map((item) => {
		return {
			name: item.slotId,
			label: item.label || item.slotId,
			showDefaultAddButton: true,
		};
	});
}
